import {
  Box, Card, Paper, Stack, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AlertProvider from 'components/AlertProvider';
import CardMedia from 'components/CardMedia';
import Icon from 'components/Icon';
import Skeleton from 'components/Skeleton';
import { env } from 'constants';
import { CateringContext } from 'constants/contexts';
import useIsDesktop from 'hooks/useIsDesktop';
import useLoadedImage from 'hooks/useLoadedImage';
import React, { useContext } from 'react';
import { priceTransformer } from 'utils';
import { getMerchantImage } from 'utils/merchant';
import { pluralize } from 'utils/string';
import MobileHeader from './MobileHeader';

function CateringHeader({
  merchant,
  loading,
  subheaderId,
  ...props
}) {
  const isDesktop = useIsDesktop();
  const catering = useContext(CateringContext);
  const logo = useLoadedImage(merchant.logo);

  const alerts = AlertProvider.useAlerts(AlertProvider.POSITIONS.MDP);

  const renderDeliveryPickup = () => {
    const showWithValue = merchant?.distance || merchant.delivery_fee;
    return (
      <Stack direction={showWithValue ? 'column' : 'row'} spacing={showWithValue ? 0 : 2} sx={{ justifyContent: { xs: 'start', lg: 'end' } }}>
        {merchant?.has_delivery && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" height={32} spacing={1}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {!loading && <Icon name="motor-filled" size={18} sx={{ color: 'text.secondary' }} />}
              <Typography variant="body-md" color="text.secondary">Delivery</Typography>
            </Stack>
            {(Boolean(merchant.distance) || Boolean(merchant.delivery_fee)) && (
              <Typography variant="body-md" color="text.secondary" sx={{ m: 0 }} noWrap>
                {merchant.distance ? ` ${merchant.distance} ${pluralize('mile', merchant.distance)}` : merchant.delivery_fee ? '' : '-'}
                {Boolean(merchant.distance) && Boolean(merchant.delivery_fee) && ' · '}
                {Boolean(merchant.delivery_fee) && `${priceTransformer(merchant.delivery_fee)} & Up`}
              </Typography>
            )}
          </Stack>

        )}
        {merchant?.has_pickup && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" height={32} spacing={1}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {!loading && <Icon name="walk-filled" size={18} sx={{ color: 'text.secondary' }} />}
              <Typography variant="body-md" color="text.secondary">Pick up</Typography>
            </Stack>
            {Boolean(merchant?.distance) && (
              <Stack direction="row" gap={2}>
                <Typography variant="body-md" color="text.secondary">
                  {`${merchant.distance} ${pluralize('mile', merchant.distance)}`}
                </Typography>
                <Typography
                  variant="label-md"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => window.open(`https://maps.google.com/?q=${merchant.location.lat},${merchant.location.lng}`)}
                >
                  Show map
                </Typography>
              </Stack>
            )}

          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <>
      {!isDesktop && (
        <MobileHeader id={subheaderId} loading={!merchant?.id} />
      )}
      {isDesktop && alerts.length > 0 && (
        <Stack
          gap={1}
          mb={1}
          position="sticky"
          id={subheaderId}
          component={Paper}
          elevation={0}
          sx={{
            p: {
              xs: 0,
              md: '16px 32px 0px 40px',
            },
            top: 84,
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          {alerts}
        </Stack>
      )}
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          p: {
            xs: 0,
            md: '16px 32px 0px 40px',
          },
          borderRadius: 0,
        }}
        {...props}
      >

        <CardMedia
          alt={merchant.name}
          loading={loading}
          image={getMerchantImage(merchant, 'md') || merchant.logo}
          sx={{
            height: {
              lg: 240,
              md: 200,
              xs: 136,
            },
            borderRadius: {
              xs: 0,
              md: 6,
            },
            alignItems: 'end',
            justifyContent: 'start',
          // marginTop: isDesktop ? 0 : (hasError ? 11 : 7),
          }}
        >
          <Stack
            alignItems="start"
            spacing={1}
            sx={{
              position: 'relative',
              zIndex: 2,
              px: {
                xs: 2,
                md: 1.5,
              },
              pb: 1.5,
            }}
          >
            <Paper
              sx={{
                width: 128,
                height: 72,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 4,
              }}
            >
              {!loading && (
                <img
                  src={logo.image}
                  alt={merchant.name}
                  style={{
                    objectFit: 'contain',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    overflow: 'hidden',
                  }}
                />
              )}
            </Paper>
            {
              env.IS_MARKETPLACE && (
                <Stack
                  sx={{
                    backgroundColor: '#fff', px: 1.5, py: 1, borderRadius: 3,
                  }}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  width="100%"
                >
                  <Typography variant="body-caption" color="black" noWrap>Local partner</Typography>
                  <img
                    src={catering.PartnerLogos[merchant?.organization_id]}
                    alt="Logo"
                    style={{
                      height: 20,
                      maxWidth: 80,
                      objectFit: 'contain',
                    }}
                  />
                </Stack>
              )
            }

          </Stack>
        </CardMedia>
        <Grid
          container
          spacing={2}
          my={1.5}
          mx={{ xs: 2, md: 0 }}
          px={{
            md: 1.5,
          }}
        >
          <Grid size={{ xs: 12, lg: 8 }}>
            <Stack
              alignItems="start"
              gap={1}

            >
              {env.IS_MARKETPLACE && (
                <Typography variant="title-h5" component="h1" align="center">
                  {merchant.name}
                </Typography>
              )}
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                  {merchant.rating > 0 && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Icon name="star-filled" />
                      <Typography variant="body-md">{merchant.rating}</Typography>
                    </Stack>
                  )}
                  {
                    merchant.tags?.length > 0 && (
                      <Typography variant="body-md" align="center">
                        {merchant.rating > 0 && ' · '}
                        {merchant.tags.map((t) => t.title).join(' · ')}
                      </Typography>
                    )
                  }
                </Stack>
                <Stack direction="row" flexWrap="wrap" spacing={2}>
                  <Typography color="text.secondary" variant="body-md" align="center">
                    {merchant.address}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            {renderDeliveryPickup()}
          </Grid>
        </Grid>

      </Card>
    </>

  );
}

function CateringHeaderSkeleton(props) {
  return (
    <Skeleton>
      <CateringHeader
        {...props}
        merchant={Skeleton.props({
          has_delivery: true,
          distance: ['string', { length: 2 }],
          delivery_fee: 'price',
          has_pickup: true,
          name: ['string', { length: 15 }],
          address: ['string', { length: 40 }],
          tags: ['array', {
            length: 3,
            value: {
              title: 'string',
            },
          }],
          takeout: true,
        })}
      />
    </Skeleton>
  );
}

export default Skeleton.withSkeleton(CateringHeader, CateringHeaderSkeleton);
